import { type FC, useCallback, useContext, useEffect } from "react";
import { generatePath, useLocation, useNavigate } from "react-router-dom";

import { Card, Step, Stepper } from "@snapchat/snap-design-system";

import { AbsoluteRoute } from "../../../../core/routes";
import { Flexbox } from "../../../../shared/components";
import { API, ENDPOINTS } from "../../../api/util";
import { ApisContext } from "../../../context";
import { useSaveRemoteApiSpec } from "../../../hooks";
import { mapRemoteApiFormValues } from "../../common/schema/mapRemoteApiFormValues";
import {
    EditRemoteApiFormik,
    apisPageIsValid,
    initialRemoteApiSpecValues,
    remoteApiSpecSchema,
} from "../../common/schema/remoteApiSpecSchema";
import { CreateApiHeader } from "../header";
import { ApiSettingsStepLabel, EndpointsStepLabel, SubmitApiButtonCta } from "../messages.generated";

import { CreateApiForm } from "./CreateApiForm";
import {
    CreateApiPageContainer,
    Footer,
    PageSectionContainer,
    pageSectionHeight,
} from "./CreateApiFormikWrapper.styles";

// TODO(LOOK-160933): - move text to messages
export const CreateApiFormikWrapper: FC = () => {
    const { pathname } = useLocation();
    const { saveRemoteApiSpec, updatedSpecId, loading } = useSaveRemoteApiSpec();
    const navigate = useNavigate();

    useEffect(() => {
        if (updatedSpecId) {
            const route = generatePath(AbsoluteRoute.API, { apiId: updatedSpecId });
            navigate(route);
        }
    }, [updatedSpecId, navigate]);

    // TODO(LOOK-160933): - clean up the context
    const { step, setStep, agreement } = useContext(ApisContext);

    const handleChangeStep = useCallback(
        (nextStep: string) => {
            if (nextStep === ENDPOINTS || nextStep === API) {
                setStep?.(nextStep);
            }
        },
        [setStep],
    );

    return (
        <EditRemoteApiFormik
            initialValues={initialRemoteApiSpecValues}
            onSubmit={(values) => {
                void saveRemoteApiSpec(mapRemoteApiFormValues(values));
            }}
            validationSchema={remoteApiSpecSchema}
            validateOnMount={true}
        >
            {({ submitForm, errors }) => (
                <>
                    <Flexbox justifyContent="center" flexDirection="column" className={pageSectionHeight}>
                        <PageSectionContainer>
                            <CreateApiPageContainer>
                                <CreateApiHeader />
                                <Card>
                                    <CreateApiForm onSubmit={submitForm} />
                                </Card>
                            </CreateApiPageContainer>
                        </PageSectionContainer>
                        {pathname === String(AbsoluteRoute.APIS_CREATE) && (
                            <Footer>
                                <Stepper
                                    activeStep={step}
                                    showNextButton
                                    showBackButton
                                    disableNext={
                                        step === API
                                            ? !apisPageIsValid(errors)
                                            : Object.keys(errors).length > 0 || !agreement
                                    }
                                    lastStepActionText={<SubmitApiButtonCta />}
                                    hideFirstStepBackButton
                                    onChangeStep={(value: string) => {
                                        handleChangeStep(value);
                                    }}
                                    onLastStepActionClick={() => {
                                        void submitForm();
                                    }}
                                    disableNextIsPending={loading}
                                >
                                    <Step key={API} value={API}>
                                        <ApiSettingsStepLabel />
                                    </Step>
                                    <Step key={ENDPOINTS} value={ENDPOINTS}>
                                        <EndpointsStepLabel />
                                    </Step>
                                </Stepper>
                            </Footer>
                        )}
                    </Flexbox>
                </>
            )}
        </EditRemoteApiFormik>
    );
};
